import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { emailService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import type { EmailCredential } from '../../types/email';
import api from '../../lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Modal,
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  FormTextarea,
  Badge,
} from '../ui';

function EmailCredentialsManager() {
  const [modal, setModal] = useState<{ type: 'create' | 'edit' | ''; data?: EmailCredential }>({
    type: '',
  });
  const queryClient = useQueryClient();

  // Get credentials
  const { data: credentials } = useQuery({
    queryKey: ['email-credentials'],
    queryFn: () => emailService.getCredentials(),
  });

  // Get user accounts for permissions
  const { data: userAccounts } = useQuery({
    queryKey: ['user-accounts'],
    queryFn: async () => {
      const response = await api.get('/users/accounts');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<EmailCredential>) => emailService.createCredential(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-credentials'] });
      messageHandler.success('Credencial criada com sucesso!');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmailCredential> }) =>
      emailService.updateCredential(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-credentials'] });
      messageHandler.success('Credencial atualizada com sucesso!');
      setModal({ type: '' });
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailService.deleteCredential(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-credentials'] });
      messageHandler.success('Credencial excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      name: formData.get('name') as string,
      imap_host: formData.get('imap_host') as string,
      imap_port: Number(formData.get('imap_port')),
      imap_username: formData.get('imap_username') as string,
      imap_password: formData.get('imap_password') as string,
      use_tls: formData.get('use_tls') === 'on',
      smtp_host: formData.get('smtp_host') as string,
      smtp_port: Number(formData.get('smtp_port')),
      smtp_username: formData.get('smtp_username') as string,
      smtp_password: formData.get('smtp_password') as string,
      smtp_secure: formData.get('smtp_secure') === 'on',
      signature: formData.get('signature') as string,
      allowed_user_accounts: Array.from(formData.getAll('allowed_user_accounts')).map(Number),
    };

    if (modal.type === 'edit' && modal.data) {
      // Only include password fields if they are not empty
      const updateData = {
        ...data,
        imap_password: data.imap_password || undefined,
        smtp_password: data.smtp_password || undefined,
      };
      updateMutation.mutate({ id: modal.data.id, data: updateData });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (credential: EmailCredential) => {
    messageHandler.confirm(
      'Excluir Credencial',
      'Tem certeza que deseja excluir esta credencial?',
      () => deleteMutation.mutate(credential.id)
    );
  };

  return (
    <>
      <div className="mb-4">
        <Button onClick={() => setModal({ type: 'create' })}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Credencial
        </Button>
      </div>

      {credentials?.length > 0 ? (
        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Host IMAP</TableHead>
                <TableHead>Host SMTP</TableHead>
                <TableHead>TLS</TableHead>
                <TableHead className="relative">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {credentials.map(credential => (
                <TableRow key={credential.id}>
                  <TableCell>{credential.name}</TableCell>
                  <TableCell>{credential.imap_username}</TableCell>
                  <TableCell>{credential.imap_host}</TableCell>
                  <TableCell>{credential.smtp_host}</TableCell>
                  <TableCell>
                    {credential.use_tls ? (
                      <Badge variant="success">Ativo</Badge>
                    ) : (
                      <Badge variant="warning">Inativo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        onClick={() => setModal({ type: 'edit', data: credential })}
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleDelete(credential)}
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon={<Shield className="h-12 w-12" />}
          title="Nenhuma credencial encontrada"
          description="Adicione suas credenciais de email para começar."
          action={{
            label: 'Adicionar Credencial',
            onClick: () => setModal({ type: 'create' }),
          }}
        />
      )}

      <Modal
        isOpen={modal.type !== ''}
        onClose={() => setModal({ type: '' })}
        title={modal.type === 'create' ? 'Nova Credencial' : 'Editar Credencial'}
        size="xl"
      >
        <Form onSubmit={handleSubmit}>
          <FormField>
            <FormLabel htmlFor="name" required>
              Nome da Conta
            </FormLabel>
            <FormInput id="name" name="name" defaultValue={modal.data?.name} required />
          </FormField>

          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Configurações IMAP</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField>
                  <FormLabel htmlFor="imap_host" required>
                    Host IMAP
                  </FormLabel>
                  <FormInput
                    id="imap_host"
                    name="imap_host"
                    defaultValue={modal.data?.imap_host}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="imap_port" required>
                    Porta IMAP
                  </FormLabel>
                  <FormInput
                    type="number"
                    id="imap_port"
                    name="imap_port"
                    defaultValue={modal.data?.imap_port || 993}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="imap_username" required>
                    Username IMAP
                  </FormLabel>
                  <FormInput
                    type="email"
                    id="imap_username"
                    name="imap_username"
                    defaultValue={modal.data?.imap_username}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="imap_password" required={!modal.data}>
                    {modal.type === 'edit' ? 'Nova Senha IMAP' : 'Senha IMAP'}
                  </FormLabel>
                  <FormInput
                    type="password"
                    id="imap_password"
                    name="imap_password"
                    required={modal.type !== 'edit'}
                  />
                </FormField>
              </div>

              <FormField className="mt-4">
                <div className="flex items-center">
                  <FormCheckbox
                    id="use_tls"
                    name="use_tls"
                    defaultChecked={modal.data?.use_tls ?? true}
                    value="on"
                  />
                  <FormLabel htmlFor="use_tls" className="ml-2 mb-0">
                    Usar TLS
                  </FormLabel>
                </div>
              </FormField>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Configurações SMTP</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField>
                  <FormLabel htmlFor="smtp_host" required>
                    Host SMTP
                  </FormLabel>
                  <FormInput
                    id="smtp_host"
                    name="smtp_host"
                    defaultValue={modal.data?.smtp_host}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="smtp_port" required>
                    Porta SMTP
                  </FormLabel>
                  <FormInput
                    type="number"
                    id="smtp_port"
                    name="smtp_port"
                    defaultValue={modal.data?.smtp_port || 587}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="smtp_username" required>
                    Username SMTP
                  </FormLabel>
                  <FormInput
                    type="email"
                    id="smtp_username"
                    name="smtp_username"
                    defaultValue={modal.data?.smtp_username}
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="smtp_password" required={!modal.data}>
                    {modal.type === 'edit' ? 'Nova Senha SMTP' : 'Senha SMTP'}
                  </FormLabel>
                  <FormInput
                    type="password"
                    id="smtp_password"
                    name="smtp_password"
                    required={modal.type !== 'edit'}
                  />
                </FormField>
              </div>

              <FormField className="mt-4">
                <div className="flex items-center">
                  <FormCheckbox
                    id="smtp_secure"
                    name="smtp_secure"
                    defaultChecked={modal.data?.smtp_secure ?? true}
                    value="on"
                  />
                  <FormLabel htmlFor="smtp_secure" className="ml-2 mb-0">
                    Usar SSL/TLS
                  </FormLabel>
                </div>
              </FormField>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Assinatura</h3>
              <FormField>
                <FormLabel htmlFor="signature">Assinatura de Email</FormLabel>
                <FormTextarea
                  id="signature"
                  name="signature"
                  defaultValue={modal.data?.signature}
                  rows={4}
                />
              </FormField>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Permissões</h3>
              <FormField>
                <FormLabel>Usuários Autorizados</FormLabel>
                <div className="border rounded-md p-4 space-y-2 max-h-48 overflow-y-auto">
                  {userAccounts?.map((account: any) => (
                    <div key={account.id} className="flex items-center">
                      <FormCheckbox
                        id={`user-${account.id}`}
                        name="allowed_user_accounts"
                        value={account.id}
                        defaultChecked={modal.data?.allowed_user_accounts?.includes(account.id)}
                      />
                      <FormLabel htmlFor={`user-${account.id}`} className="ml-2 mb-0">
                        {account.name}
                      </FormLabel>
                    </div>
                  ))}
                </div>
              </FormField>
            </div>
          </div>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
              className="sm:col-start-2"
            >
              {modal.type === 'create' ? 'Criar' : 'Salvar'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setModal({ type: '' })}
              className="sm:col-start-1"
            >
              Cancelar
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}

export default EmailCredentialsManager;
